
import React, { useCallback, useEffect, useRef, useState } from "react";

const API_BASE = "https://api.welbeing.in";

const getYoutubeEmbedUrl = (link) => {
    if (!link?.trim()) return null;

    try {
        const url = new URL(link.trim());
        const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
        let videoId = "";

        if (hostname === "youtu.be") {
            videoId = url.pathname.split("/").filter(Boolean)[0] || "";
        } else if (
            hostname === "youtube.com" ||
            hostname === "m.youtube.com" ||
            hostname === "youtube-nocookie.com" ||
            hostname === "music.youtube.com"
        ) {
            if (url.pathname === "/watch") {
                videoId = url.searchParams.get("v") || "";
            } else {
                const match = url.pathname.match(
                    /^\/(?:embed|shorts|live)\/([^/]+)/
                );
                videoId = match?.[1] || "";
            }
        } else {
            return null;
        }

        // Standard YouTube video IDs contain 11 URL-safe characters.
        if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return null;

        return `https://www.youtube-nocookie.com/embed/${videoId}`;
    } catch {
        return null;
    }
};

const readApiResponse = async (response) => {
    let data;

    try {
        data = await response.json();
    } catch {
        throw new Error("The server returned an invalid response.");
    }

    if (!response.ok) {
        throw new Error(
            data?.message || `Request failed (${response.status}).`
        );
    }

    return data;
};

const Video = () => {
    const [videoType, setVideoType] = useState("file");
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [youtubeLink, setYoutubeLink] = useState("");
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");

    const fileInputRef = useRef(null);
    const submittingRef = useRef(false);
    const deletingRef = useRef(new Set());

    // Release temporary preview URLs when the preview changes or unmounts.
    useEffect(() => {
        if (!videoPreview) return undefined;

        return () => URL.revokeObjectURL(videoPreview);
    }, [videoPreview]);

    const fetchVideos = useCallback(async (signal) => {
        const response = await fetch(`${API_BASE}/home/get-videos/`, {
            signal,
        });

        const data = await readApiResponse(response);

        if (data.status !== "success" || !Array.isArray(data.data)) {
            throw new Error(data.message || "Failed to fetch videos.");
        }

        setVideos(data.data);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        const loadVideos = async () => {
            setLoading(true);
            setError("");

            try {
                await fetchVideos(controller.signal);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Unable to load videos.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadVideos();

        return () => controller.abort();
    }, [fetchVideos]);

    const handleVideoFileChange = (event) => {
        const file = event.target.files?.[0];

        setError("");
        setSuccessMessage("");

        if (!file) {
            setVideoFile(null);
            setVideoPreview(null);
            return;
        }

        if (!file.type.startsWith("video/")) {
            setVideoFile(null);
            setVideoPreview(null);
            event.target.value = "";
            setError("Please select a valid video file.");
            return;
        }

        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));
    };

    const handleTypeSwitch = (type) => {
        if (type !== "file" && type !== "youtube") return;

        setVideoType(type);
        setVideoFile(null);
        setVideoPreview(null);
        setYoutubeLink("");
        setError("");
        setSuccessMessage("");

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submittingRef.current) return;

        setError("");
        setSuccessMessage("");

        if (videoType === "file" && !videoFile) {
            setError("Please select a video file.");
            return;
        }

        if (videoType === "youtube" && !youtubeLink.trim()) {
            setError("Please enter a YouTube link.");
            return;
        }

        if (
            videoType === "youtube" &&
            !getYoutubeEmbedUrl(youtubeLink)
        ) {
            setError(
                "Please enter a valid YouTube video URL, such as https://www.youtube.com/watch?v=XXXXXXXXXXX."
            );
            return;
        }

        submittingRef.current = true;
        setAdding(true);

        try {
            const formData = new FormData();
            formData.append("video_type", videoType);

            if (videoType === "file") {
                formData.append("video", videoFile);
            } else {
                formData.append("video_link", youtubeLink.trim());
            }

            const response = await fetch(
                `${API_BASE}/admin/home/add-video/`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            const data = await readApiResponse(response);

            if (data.status !== "success") {
                throw new Error(data.message || "Failed to add video.");
            }

            setSuccessMessage(
                videoType === "file"
                    ? "Video uploaded successfully!"
                    : "YouTube video added successfully!"
            );

            setVideoFile(null);
            setVideoPreview(null);
            setYoutubeLink("");

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

            try {
                await fetchVideos();
            } catch (err) {
                setError(
                    `Video was added, but the list could not be refreshed. ${err.message}`
                );
            }
        } catch (err) {
            setError(err.message || "Failed to add video.");
        } finally {
            submittingRef.current = false;
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (id == null || deletingRef.current.has(id)) return;

        const confirmed = window.confirm(
            "Are you sure you want to remove this video?"
        );

        if (!confirmed) return;

        deletingRef.current.add(id);
        setDeletingId(id);
        setError("");
        setSuccessMessage("");

        try {
            const response = await fetch(
                `${API_BASE}/home/delete-videos/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ id }),
                }
            );

            const data = await readApiResponse(response);

            if (data.status !== "success") {
                throw new Error(data.message || "Failed to delete video.");
            }

            setVideos((previous) =>
                previous.filter((video) => video.id !== id)
            );

            setSuccessMessage("Video removed successfully!");
        } catch (err) {
            setError(err.message || "Failed to delete video.");
        } finally {
            deletingRef.current.delete(id);
            setDeletingId(null);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {successMessage && (
                                <div
                                    className="alert alert-success alert-dismissible fade show"
                                    role="alert"
                                >
                                    {successMessage}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setSuccessMessage("")}
                                    />
                                </div>
                            )}

                            {error && (
                                <div
                                    className="alert alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    {error}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setError("")}
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <h5
                                    className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                                    style={{
                                        fontSize: "30px",
                                        background:
                                            "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                                        borderLeft: "5px solid #0d6efd",
                                        color: "#0d6efd",
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    Add Video
                                </h5>
                            </div>

                            <div className="d-flex gap-2 mb-4 flex-wrap">
                                <button
                                    type="button"
                                    className={`btn ${
                                        videoType === "file"
                                            ? "btn-primary"
                                            : "btn-outline-primary"
                                    }`}
                                    onClick={() => handleTypeSwitch("file")}
                                >
                                    <i className="ti ti-video me-2" />
                                    Video Upload
                                </button>

                                <button
                                    type="button"
                                    className={`btn ${
                                        videoType === "youtube"
                                            ? "btn-danger"
                                            : "btn-outline-danger"
                                    }`}
                                    onClick={() =>
                                        handleTypeSwitch("youtube")
                                    }
                                >
                                    <i className="ti ti-brand-youtube me-2" />
                                    YouTube Link
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="form-body">
                                    <div className="row align-items-end">
                                        {videoType === "file" && (
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="videoFile"
                                                        className="form-label"
                                                    >
                                                        Select Video File
                                                    </label>

                                                    <input
                                                        id="videoFile"
                                                        type="file"
                                                        className="form-control"
                                                        accept="video/*"
                                                        ref={fileInputRef}
                                                        onChange={
                                                            handleVideoFileChange
                                                        }
                                                        disabled={adding}
                                                    />

                                                    {videoFile && (
                                                        <span className="badge bg-primary mt-2">
                                                            <i className="ti ti-check me-1" />
                                                            {videoFile.name}
                                                        </span>
                                                    )}

                                                    {videoPreview && (
                                                        <div className="mt-2 position-relative d-inline-block">
                                                            <video
                                                                src={videoPreview}
                                                                controls
                                                                preload="metadata"
                                                                style={{
                                                                    width: "280px",
                                                                    maxWidth: "100%",
                                                                    height: "160px",
                                                                    borderRadius: "8px",
                                                                    border: "1px solid #dee2e6",
                                                                    display: "block",
                                                                    objectFit: "contain",
                                                                }}
                                                            />

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                aria-label="Remove selected video"
                                                                disabled={adding}
                                                                style={{
                                                                    position: "absolute",
                                                                    top: "-8px",
                                                                    right: "-8px",
                                                                    borderRadius: "50%",
                                                                    width: "26px",
                                                                    height: "26px",
                                                                    padding: "0",
                                                                }}
                                                                onClick={() => {
                                                                    setVideoFile(null);
                                                                    setVideoPreview(null);

                                                                    if (
                                                                        fileInputRef.current
                                                                    ) {
                                                                        fileInputRef.current.value =
                                                                            "";
                                                                    }
                                                                }}
                                                            >
                                                                <i className="ti ti-x" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {videoType === "youtube" && (
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="youtubeLink"
                                                        className="form-label"
                                                    >
                                                        YouTube Video Link
                                                    </label>

                                                    <div className="input-group">
                                                        <span className="input-group-text bg-danger text-white">
                                                            <i className="ti ti-brand-youtube" />
                                                        </span>

                                                        <input
                                                            id="youtubeLink"
                                                            type="url"
                                                            className="form-control"
                                                            placeholder="https://www.youtube.com/watch?v=..."
                                                            value={youtubeLink}
                                                            onChange={(event) => {
                                                                setYoutubeLink(
                                                                    event.target.value
                                                                );
                                                                setError("");
                                                                setSuccessMessage("");
                                                            }}
                                                            disabled={adding}
                                                        />
                                                    </div>

                                                    {youtubeLink.trim() && (
                                                        getYoutubeEmbedUrl(
                                                            youtubeLink
                                                        ) ? (
                                                            <div className="mt-2">
                                                                <iframe
                                                                    src={getYoutubeEmbedUrl(
                                                                        youtubeLink
                                                                    )}
                                                                    width="280"
                                                                    height="160"
                                                                    style={{
                                                                        maxWidth: "100%",
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #dee2e6",
                                                                        display: "block",
                                                                    }}
                                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                    allowFullScreen
                                                                    loading="lazy"
                                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                                    title="YouTube video preview"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <small className="text-muted d-block mt-2">
                                                                Enter a valid YouTube video URL to see its preview.
                                                            </small>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-md-2">
                                            <div className="mb-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-light-primary text-primary font-medium w-100"
                                                    disabled={adding}
                                                >
                                                    {adding ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                            Adding…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ti ti-plus me-1" />
                                                            Add
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>

                            <hr />

                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h5 className="mb-0">Videos List</h5>
                                <span className="badge bg-dark fs-6">
                                    {videos.length}{" "}
                                    {videos.length === 1 ? "Video" : "Videos"}
                                </span>
                            </div>

                            <div
                                className="table-responsive"
                                style={{ overflowX: "auto" }}
                            >
                                <table className="table border table-striped table-bordered align-middle">
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Type</th>
                                            <th>Preview</th>
                                            <th className="text-center">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="text-center py-4"
                                                >
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    Loading videos…
                                                </td>
                                            </tr>
                                        ) : videos.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="text-center fw-bold py-4"
                                                >
                                                    No videos added yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            videos.map((video, index) => {
                                                const isYoutube =
                                                    video.video_type ===
                                                    "youtube";
                                                const embedUrl = isYoutube
                                                    ? getYoutubeEmbedUrl(
                                                          video.video_link
                                                      )
                                                    : null;

                                                return (
                                                    <tr key={video.id}>
                                                        <td>{index + 1}</td>

                                                        <td>
                                                            {isYoutube ? (
                                                                <span className="badge bg-danger">
                                                                    <i className="ti ti-brand-youtube me-1" />
                                                                    YouTube
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-primary">
                                                                    <i className="ti ti-video me-1" />
                                                                    Video File
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td>
                                                            {isYoutube ? (
                                                                embedUrl ? (
                                                                    <iframe
                                                                        src={embedUrl}
                                                                        width="160"
                                                                        height="90"
                                                                        style={{
                                                                            maxWidth: "100%",
                                                                            borderRadius: "6px",
                                                                            border: "1px solid #dee2e6",
                                                                            display: "block",
                                                                        }}
                                                                        allowFullScreen
                                                                        loading="lazy"
                                                                        title={`YouTube video ${index + 1}`}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted">
                                                                        Invalid YouTube link
                                                                    </span>
                                                                )
                                                            ) : video.video_file ? (
                                                                <video
                                                                    src={`${API_BASE}/uploads/videos/${encodeURIComponent(
                                                                        video.video_file
                                                                    )}`}
                                                                    controls
                                                                    preload="metadata"
                                                                    style={{
                                                                        width: "160px",
                                                                        maxWidth: "100%",
                                                                        height: "90px",
                                                                        borderRadius: "6px",
                                                                        border: "1px solid #dee2e6",
                                                                        objectFit: "contain",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted">
                                                                    Video unavailable
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        video.id
                                                                    )
                                                                }
                                                                disabled={
                                                                    deletingId ===
                                                                    video.id
                                                                }
                                                                aria-label={`Delete video ${index + 1}`}
                                                            >
                                                                {deletingId ===
                                                                video.id ? (
                                                                    <>
                                                                        <span
                                                                            className="spinner-border spinner-border-sm me-1"
                                                                            role="status"
                                                                            aria-hidden="true"
                                                                        />
                                                                        Removing…
                                                                    </>
                                                                ) : (
                                                                    <i className="ti ti-trash" />
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {!loading && error && videos.length === 0 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={async () => {
                                        setLoading(true);
                                        setError("");

                                        try {
                                            await fetchVideos();
                                        } catch (err) {
                                            setError(
                                                err.message ||
                                                    "Unable to load videos."
                                            );
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    <i className="ti ti-refresh me-1" />
                                    Retry loading videos
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Video;
